import ArticleLevel from "@/types/models/ArticleLevel.interface";
import {
  Assignment,
  AssignmentMember,
  NestedAssignmentMember,
} from "@/types/models/Assignment.interface";
import {
  ILegacyStudentReadingLevel,
  IRecommendedReadingLevel,
} from "@/types/models/ReadingLevel.interface";
import { User } from "@/types/models/User.interface";
import getUserProperties from "@/users/getUserProperties";
import getCurrentLevelByNumericGrade from "@/utils/getCurrentLevelByNumericGrade";
import findDefaultLevelFromReadingOrGradeLevel from "@/view/utils/findDefaultLevelFromReadingOrGradeLevel";
import findLevelLockedContent from "@/view/utils/findLevelLockedContent";
import getCurrentLanguage from "./utils/getCurrentLanguage";

const getCurrentLevel = ({
  articleLevels = [],
  contentId,
  levelId = "",
  matchedAssignment,
  user,
  studentReadingLevel,
}: {
  articleLevels?: ArticleLevel[] | undefined;
  contentId: string;
  levelId?: string | undefined;
  matchedAssignment: Assignment | undefined;
  user: User;
  studentReadingLevel:
    | IRecommendedReadingLevel
    | ILegacyStudentReadingLevel
    | undefined;
}) => {
  const { isStudent } = getUserProperties(user);
  const assignmentMembers: AssignmentMember[] | NestedAssignmentMember[] =
    matchedAssignment?.assignment_members || [];
  const sortedLevels = [...articleLevels].sort(
    (a, b) => a.raw_lexile - b.raw_lexile,
  );
  const level = sortedLevels.find((level) => level.id === levelId);
  const currentLangCode = getCurrentLanguage({
    assignmentMembers,
    level,
    studentReadingLevel,
  });

  // Uses new field.
  if (isStudent && matchedAssignment?.default_reading_level) {
    // This logic will need to be updated once the MVE flow supports multi-asset assignments,
    // to allow for the possibility that there is not a level that directly matches the default reading level.
    const level = getCurrentLevelByNumericGrade(
      sortedLevels,
      matchedAssignment?.default_reading_level,
      currentLangCode,
    );
    if (level) {
      return level;
    }
  }

  // Uses legacy field.
  if (isStudent && matchedAssignment?.grade_level_lock) {
    return findLevelLockedContent(matchedAssignment, sortedLevels, contentId);
  }

  // Uses new fields.
  if (
    isStudent &&
    !matchedAssignment?.default_reading_level &&
    matchedAssignment?.is_reading_level_locked
  ) {
    return findDefaultLevelFromReadingOrGradeLevel(
      user,
      sortedLevels,
      currentLangCode,
      studentReadingLevel,
    );
  }

  if (level?.language === currentLangCode) {
    return level;
  }

  return findDefaultLevelFromReadingOrGradeLevel(
    user,
    sortedLevels,
    currentLangCode,
    studentReadingLevel,
  );
};

export default getCurrentLevel;
